import React, { useState, useEffect } from 'react';
import { Table, Row, Col, Typography, Card, Statistic, Space, Button, Empty, message, Spin, Tag, Avatar, Select, Form, Input, Divider, List, Badge } from 'antd';
import {
  FileExcelOutlined, DollarOutlined,
  YoutubeOutlined, CheckCircleOutlined, ClockCircleOutlined, UserOutlined,
  LockOutlined, SaveOutlined, KeyOutlined, MessageOutlined, SendOutlined, PhoneOutlined,
  FilePdfOutlined, DownloadOutlined
} from '@ant-design/icons';
import api from '../utils/api';
import { exportExcel, exportPdf } from '../utils/reportExport';

const { Title, Text } = Typography;

// Month names -> a sortable rank, so "May", "2024-05", "May 2024" all order correctly.
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
function monthRank(m) {
  if (!m) return 0;
  const s = String(m).toLowerCase();
  let mt = s.match(/(\d{4})[-/.](\d{1,2})/);          // 2024-05
  if (mt) return parseInt(mt[1], 10) * 12 + parseInt(mt[2], 10);
  mt = s.match(/(\d{1,2})[-/.](\d{4})/);              // 05/2024
  if (mt) return parseInt(mt[2], 10) * 12 + parseInt(mt[1], 10);
  const yr = (s.match(/(\d{4})/) || [])[1];
  const mon = MONTHS.findIndex((x) => s.includes(x)); // May / May 2024
  if (mon >= 0) return (yr ? parseInt(yr, 10) : 2000) * 12 + (mon + 1);
  return 0;
}
// Distinct months present in revenue, newest first.
function distinctMonths(rows) {
  const set = [];
  (rows || []).forEach((r) => { const m = (r.month || '').trim(); if (m && !set.includes(m)) set.push(m); });
  return set.sort((a, b) => monthRank(b) - monthRank(a));
}

const fmtInr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtUsd = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

function downloadRows(filename, rows) {
  if (!rows.length) { message.info('Nothing to export'); return; }
  const headers = Object.keys(rows[0]);
  const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
  const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => esc(r[h])).join(','))].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// Dependency-free monthly bar chart of client share (₹).
function MonthBars({ rows }) {
  const byMonth = {};
  (rows || []).forEach((r) => {
    const m = r.month || '—';
    byMonth[m] = (byMonth[m] || 0) + Number(r.client_share_inr || 0);
  });
  const data = Object.keys(byMonth).map((m) => ({ label: m, value: byMonth[m] }));
  if (!data.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No earnings yet" />;
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, height: 200, overflowX: 'auto', paddingTop: 8 }}>
      {data.map((d, i) => {
        const h = Math.max(4, (d.value / max) * 150);
        return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 0 64px' }}>
            <Text style={{ fontSize: 12, marginBottom: 4, whiteSpace: 'nowrap' }}>{fmtInr(d.value)}</Text>
            <div title={`${d.label}: ${fmtInr(d.value)}`} style={{ width: 38, height: h, background: 'linear-gradient(180deg,#667eea,#764ba2)', borderRadius: '6px 6px 0 0' }} />
            <Text style={{ fontSize: 12, marginTop: 6, whiteSpace: 'nowrap' }}>{d.label}</Text>
          </div>
        );
      })}
    </div>
  );
}

// Applied to the card BODY (the global .ant-card !important white bg overrides the root).
const cardBody = (grad) => ({ background: grad, borderRadius: 14 });

export default function ClientDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');     // 'all' | 'last2' | 'last3' | 'last6' | a specific month
  const [profileForm] = Form.useForm();
  const [pwdForm] = Form.useForm();
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [msgBody, setMsgBody] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [reportMonths, setReportMonths] = useState([]);  // months selected for the "My Report Download" section

  useEffect(() => { fetchMe(); fetchMsgs(); }, []);

  const fetchMe = async () => {
    setLoading(true);
    try {
      const res = await api.get('/clients/me');
      setData(res.data);
      profileForm.setFieldsValue({ name: res.data.client?.name, phone: res.data.client?.phone });
    }
    catch (err) { message.error(err.uiMessage || 'Could not load your data'); }
    setLoading(false);
  };

  const fetchMsgs = async () => {
    try { const res = await api.get('/clients/me/messages'); setMsgs(res.data.messages || []); }
    catch (err) { /* contact section just shows empty */ }
  };

  const saveProfile = async (vals) => {
    setSavingProfile(true);
    try { await api.put('/clients/me/profile', { name: vals.name, phone: vals.phone }); message.success('Profile updated'); fetchMe(); }
    catch (err) { message.error(err.uiMessage || err.response?.data?.error || 'Failed to update profile'); }
    setSavingProfile(false);
  };

  const changePassword = async (vals) => {
    if (vals.new_password !== vals.confirm_password) { message.error('New password & confirm do not match'); return; }
    setSavingPwd(true);
    try {
      await api.post('/auth/change-password', { current_password: vals.current_password, new_password: vals.new_password });
      message.success('Password changed'); pwdForm.resetFields();
    } catch (err) { message.error(err.uiMessage || err.response?.data?.error || 'Failed to change password'); }
    setSavingPwd(false);
  };

  const sendMessage = async () => {
    if (!msgBody.trim()) { message.warning('Please type your message'); return; }
    setSendingMsg(true);
    try { await api.post('/clients/me/messages', { body: msgBody.trim() }); setMsgBody(''); message.success('Sent to admin'); fetchMsgs(); }
    catch (err) { message.error(err.uiMessage || err.response?.data?.error || 'Failed to send'); }
    setSendingMsg(false);
  };

  if (loading) return <Spin size="large" style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;
  if (!data || !data.client) {
    return <Empty description="No client profile linked to your account. Please contact admin." style={{ marginTop: 80 }} />;
  }

  const s = data.summary || {};
  const channels = data.channels || [];
  const allRevenue = data.revenue || [];
  const payments = data.payments || [];
  const paidPct = s.client_share_inr > 0 ? Math.min(100, Math.round((s.paid_inr / s.client_share_inr) * 100)) : 0;

  // --- Period filter (applies to chart, earnings detail, monthly status, download) ---
  const months = distinctMonths(allRevenue);
  let visibleMonths = months;
  if (period === 'last2') visibleMonths = months.slice(0, 2);
  else if (period === 'last3') visibleMonths = months.slice(0, 3);
  else if (period === 'last6') visibleMonths = months.slice(0, 6);
  else if (period !== 'all') visibleMonths = months.filter((m) => m === period);
  const inPeriod = (r) => period === 'all' ? true : visibleMonths.includes((r.month || '').trim());
  const revenue = allRevenue.filter(inPeriod);

  // Per-month status: compare client share (₹) vs payments recorded for that month.
  const paidByMonth = {};
  payments.forEach((p) => { const m = (p.month || '').trim(); if (m) paidByMonth[m] = (paidByMonth[m] || 0) + Number(p.amount_inr || 0); });
  const monthRows = visibleMonths.map((m) => {
    const share = allRevenue.filter((r) => (r.month || '').trim() === m).reduce((a, r) => a + Number(r.client_share_inr || 0), 0);
    const paid = paidByMonth[m] || 0;
    let status = 'pending';
    if (paid >= share && share > 0) status = 'paid';
    else if (paid > 0) status = 'partial';
    return { month: m, share, paid, balance: Math.max(share - paid, 0), status };
  });
  const periodLabel = period === 'all' ? 'all-months' : period === 'last2' ? 'last-2-months' : period === 'last3' ? 'last-3-months' : period === 'last6' ? 'last-6-months' : period;

  // --- "My Report Download" section: pick specific months, or 3/6-month presets, export PDF/Excel ---
  const pickPreset = (n) => setReportMonths(months.slice(0, n));
  const reportRows = allRevenue
    .filter((r) => reportMonths.includes((r.month || '').trim()))
    .sort((a, b) => monthRank(b.month) - monthRank(a.month))
    .map((r) => ({
      'Channel Name': r.channel_name || '',
      'URL': r.url || '',
      'Revenue ($)': Number(r.revenue_usd || 0),
      'Month': r.month || '',
      'Share ($)': Number(r.client_share_usd || 0),
      'INR (₹)': Number(r.client_share_inr || 0),
    }));
  const reportTotalInr = reportRows.reduce((a, r) => a + Number(r['INR (₹)'] || 0), 0);
  const reportFileBase = `${(data.client.name || 'client').replace(/\s+/g, '-')}-report-${reportMonths.length === 1 ? reportMonths[0] : reportMonths.length + 'months'}`.replace(/[^a-zA-Z0-9-]/g, '');
  const downloadReport = (fmt) => {
    if (!reportMonths.length) { message.warning('Pehle month select karo'); return; }
    if (!reportRows.length) { message.info('Selected month(s) me koi earning nahi mili'); return; }
    const subtitle = `${data.client.name} · Months: ${reportMonths.join(', ')} · Total ₹${reportTotalInr.toLocaleString('en-IN')}`;
    if (fmt === 'pdf') exportPdf(`${reportFileBase}.pdf`, reportRows, { title: 'My Earnings Report', subtitle });
    else exportExcel(`${reportFileBase}.xlsx`, reportRows, 'My Report');
    message.success(`Report downloaded (${fmt.toUpperCase()})`);
  };

  return (
    <div>
      {/* Welcome banner */}
      <Card
        style={{ borderRadius: 16, marginBottom: 20, overflow: 'hidden' }}
        bodyStyle={{ padding: '20px 24px', background: 'linear-gradient(120deg,#1a1a2e 0%,#3a3a6a 100%)' }}
      >
        <Row align="middle" justify="space-between" gutter={[16, 16]}>
          <Col>
            <Space size={16} align="center">
              <Avatar size={56} style={{ background: 'rgba(255,255,255,0.18)' }} icon={<UserOutlined />} />
              <div>
                <Title level={3} style={{ color: '#fff', margin: 0 }}>{data.client.name}</Title>
                <Text style={{ color: 'rgba(255,255,255,0.75)' }}>My Channels &amp; Payments Dashboard</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Tag color={s.balance_inr > 0 ? 'volcano' : 'green'} style={{ fontSize: 14, padding: '6px 14px', borderRadius: 20 }}>
              {s.balance_inr > 0 ? `Pending: ${fmtInr(s.balance_inr)}` : 'All Paid ✓'}
            </Tag>
          </Col>
        </Row>
      </Card>

      {/* Summary cards */}
      <Row gutter={[16, 16]}>
        <Col xs={12} md={8} lg={6}>
          <Card bodyStyle={cardBody('linear-gradient(135deg,#667eea22,#667eea08)')}>
            <Statistic title={<Text strong>Total Revenue ($)</Text>} value={s.revenue_usd} precision={2} prefix={<DollarOutlined style={{ color: '#667eea' }} />} formatter={(v) => fmtUsd(v)} valueStyle={{ color: '#5566d6', fontSize: 24 }} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={6}>
          <Card bodyStyle={cardBody('linear-gradient(135deg,#52c41a22,#52c41a08)')}>
            <Statistic title={<Text strong>My Share ($)</Text>} value={s.client_share_usd} precision={2} formatter={(v) => fmtUsd(v)} valueStyle={{ color: '#3f8600', fontSize: 24 }} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={6}>
          <Card bodyStyle={cardBody('linear-gradient(135deg,#13c2c222,#13c2c208)')}>
            <Statistic title={<Text strong>My Share (₹)</Text>} value={s.client_share_inr} precision={2} formatter={(v) => fmtInr(v)} valueStyle={{ color: '#08979c', fontSize: 24 }} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={6}>
          <Card bodyStyle={cardBody('linear-gradient(135deg,#52c41a22,#52c41a08)')}>
            <Statistic title={<Text strong>Paid (₹)</Text>} value={s.paid_inr} precision={2} prefix={<CheckCircleOutlined style={{ color: '#3f8600' }} />} formatter={(v) => fmtInr(v)} valueStyle={{ color: '#3f8600', fontSize: 24 }} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={6}>
          <Card bodyStyle={cardBody(s.balance_inr > 0 ? 'linear-gradient(135deg,#ff4d4f22,#ff4d4f08)' : 'linear-gradient(135deg,#52c41a22,#52c41a08)')}>
            <Statistic title={<Text strong>Balance Due (₹)</Text>} value={s.balance_inr} precision={2} prefix={s.balance_inr > 0 ? <ClockCircleOutlined style={{ color: '#cf1322' }} /> : <CheckCircleOutlined style={{ color: '#3f8600' }} />} formatter={(v) => fmtInr(v)} valueStyle={{ color: s.balance_inr > 0 ? '#cf1322' : '#3f8600', fontSize: 24 }} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={6}>
          <Card bodyStyle={cardBody('linear-gradient(135deg,#faad1422,#faad1408)')}>
            <Statistic title={<Text strong>My Channels</Text>} value={channels.length} prefix={<YoutubeOutlined style={{ color: '#d48806' }} />} valueStyle={{ color: '#d48806', fontSize: 24 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>{paidPct}% of share paid</Text>
          </Card>
        </Col>
      </Row>

      {/* Period filter toolbar */}
      <Card style={{ borderRadius: 14, marginTop: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space wrap align="center">
          <Text strong>Show period:</Text>
          <Select
            value={period}
            style={{ minWidth: 200 }}
            onChange={setPeriod}
            options={[
              { value: 'all', label: 'All months' },
              { value: 'last2', label: 'Last 2 months' },
              { value: 'last3', label: 'Last 3 months' },
              { value: 'last6', label: 'Last 6 months' },
              ...(months.length ? [{ label: '— Specific month —', options: months.map((m) => ({ value: m, label: m })) }] : []),
            ]}
          />
          <Button
            icon={<FileExcelOutlined />}
            onClick={() => downloadRows(`my-report-${periodLabel}.csv`, revenue.map((r) => ({
              'Channel Name': r.channel_name, URL: r.url, 'Revenue ($)': r.revenue_usd, Month: r.month, 'Share ($)': r.client_share_usd, 'INR (₹)': r.client_share_inr,
            })))}
          >Download Report ({periodLabel})</Button>
        </Space>
      </Card>

      {/* Monthly status: paid vs pending per month */}
      <Card title="Monthly Payment Status" style={{ borderRadius: 14, marginTop: 16 }}>
        <Table
          size="small" rowKey="month" pagination={false} scroll={{ x: 560 }}
          dataSource={monthRows}
          locale={{ emptyText: 'No months in this period' }}
          columns={[
            { title: 'Month', dataIndex: 'month', render: (m) => <Text strong>{m}</Text> },
            { title: 'My Share (₹)', dataIndex: 'share', align: 'right', render: (v) => fmtInr(v) },
            { title: 'Paid (₹)', dataIndex: 'paid', align: 'right', render: (v) => <Text style={{ color: '#3f8600' }}>{fmtInr(v)}</Text> },
            { title: 'Balance (₹)', dataIndex: 'balance', align: 'right', render: (v) => <Text strong style={{ color: v > 0 ? '#cf1322' : '#3f8600' }}>{fmtInr(v)}</Text> },
            { title: 'Status', dataIndex: 'status', align: 'center', render: (st) => (
              st === 'paid' ? <Tag icon={<CheckCircleOutlined />} color="green">Paid</Tag>
                : st === 'partial' ? <Tag icon={<ClockCircleOutlined />} color="gold">Partial</Tag>
                  : <Tag icon={<ClockCircleOutlined />} color="volcano">Pending — abhi aana baaki</Tag>
            ) },
          ]}
        />
      </Card>

      {/* Chart + channels */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card title={`Earnings by Month (₹) — ${periodLabel}`} style={{ borderRadius: 14 }}>
            <MonthBars rows={revenue} />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="My Channels" style={{ borderRadius: 14 }}>
            <Table
              size="small" rowKey="id" pagination={false}
              dataSource={channels}
              locale={{ emptyText: 'No channels linked yet' }}
              columns={[
                { title: 'Channel', dataIndex: 'channel_name', render: (n, r) => (
                  <Space direction="vertical" size={2}>
                    <Text strong>{n}</Text>
                    {r.url && <a href={r.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, wordBreak: 'break-all', whiteSpace: 'normal' }}>{r.url}</a>}
                  </Space>
                ) },
                { title: 'My Share', dataIndex: 'client_share_percent', align: 'right', render: (p) => <Tag color="blue">{p}%</Tag> },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Earnings detail */}
      <Card
        title="Earnings Detail"
        style={{ borderRadius: 14, marginTop: 16 }}
        extra={<Button icon={<FileExcelOutlined />} onClick={() => downloadRows('my-statement.csv', revenue.map((r) => ({
          'Channel Name': r.channel_name, URL: r.url, 'Revenue ($)': r.revenue_usd, Month: r.month, 'Share ($)': r.client_share_usd, 'INR (₹)': r.client_share_inr,
        })))}>Download Statement</Button>}
      >
        <Table
          rowKey="id" pagination={false} scroll={{ x: 700 }}
          dataSource={revenue}
          locale={{ emptyText: 'No earnings yet' }}
          columns={[
            { title: 'Channel Name', dataIndex: 'channel_name', render: (n) => <Text strong>{n}</Text> },
            { title: 'URL', dataIndex: 'url', render: (u) => u ? <a href={u} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}>{u}</a> : '-' },
            { title: 'Revenue ($)', dataIndex: 'revenue_usd', align: 'right', render: (v) => fmtUsd(v) },
            { title: 'Month', dataIndex: 'month' },
            { title: 'Share ($)', dataIndex: 'client_share_usd', align: 'right', render: (v) => <Text style={{ color: '#3f8600' }}>{fmtUsd(v)}</Text> },
            { title: 'INR (₹)', dataIndex: 'client_share_inr', align: 'right', render: (v) => <Text strong style={{ color: '#08979c' }}>{fmtInr(v)}</Text> },
          ]}
          summary={(rows) => {
            const tot = rows.reduce((a, r) => a + Number(r.client_share_inr || 0), 0);
            const totUsd = rows.reduce((a, r) => a + Number(r.client_share_usd || 0), 0);
            if (!rows.length) return null;
            return (
              <Table.Summary.Row style={{ background: '#fafafa' }}>
                <Table.Summary.Cell index={0} colSpan={4}><Text strong>Total</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right"><Text strong style={{ color: '#3f8600' }}>{fmtUsd(totUsd)}</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right"><Text strong style={{ color: '#08979c' }}>{fmtInr(tot)}</Text></Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Card>

      {/* Payments */}
      <Card title="Payments Received" style={{ borderRadius: 14, marginTop: 16 }}>
        <Table
          rowKey="id" pagination={false}
          dataSource={payments}
          locale={{ emptyText: 'No payments received yet' }}
          columns={[
            { title: 'Date', dataIndex: 'paid_on', render: (d) => d || '-' },
            { title: 'Month', dataIndex: 'month', render: (m) => m || '-' },
            { title: 'Amount (₹)', dataIndex: 'amount_inr', align: 'right', render: (v) => <Text strong style={{ color: '#3f8600' }}>{fmtInr(v)}</Text> },
            { title: 'Amount ($)', dataIndex: 'amount_usd', align: 'right', render: (v) => fmtUsd(v) },
            { title: 'Note', dataIndex: 'note', render: (n) => n || '-' },
          ]}
        />
      </Card>

      {/* My Report Download: pick months / 3-6 month presets, export PDF or Excel */}
      <Card title={<span><DownloadOutlined /> My Report Download</span>} style={{ borderRadius: 14, marginTop: 16 }}>
        <Text type="secondary">
          Apne months chuno (3 mahine / 6 mahine ek saath bhi), phir PDF ya Excel me report download karo.
        </Text>
        <Row gutter={[12, 12]} align="bottom" style={{ marginTop: 14 }}>
          <Col xs={24} md={14}>
            <Text strong>Select Month(s)</Text>
            <Select
              mode="multiple"
              allowClear
              value={reportMonths}
              onChange={setReportMonths}
              placeholder={months.length ? 'Choose months (e.g. May 2025, June 2025)' : 'No months available yet'}
              style={{ width: '100%', marginTop: 6 }}
              options={months.map((m) => ({ value: m, label: m }))}
              maxTagCount="responsive"
            />
          </Col>
          <Col xs={24} md={10}>
            <Text strong>Quick select</Text>
            <div style={{ marginTop: 6 }}>
              <Space wrap>
                <Button onClick={() => pickPreset(3)} disabled={!months.length}>Last 3 months</Button>
                <Button onClick={() => pickPreset(6)} disabled={!months.length}>Last 6 months</Button>
                <Button onClick={() => setReportMonths(months)} disabled={!months.length}>All months</Button>
                <Button type="link" onClick={() => setReportMonths([])} disabled={!reportMonths.length}>Clear</Button>
              </Space>
            </div>
          </Col>
        </Row>
        <Divider style={{ margin: '16px 0 12px' }} />
        <Space wrap align="center">
          <Text type="secondary">
            {reportMonths.length
              ? `${reportRows.length} row(s) · ${reportMonths.length} month(s) · Total ${fmtInr(reportTotalInr)}`
              : 'Koi month select nahi kiya'}
          </Text>
          <Button type="primary" danger icon={<FilePdfOutlined />} disabled={!reportMonths.length} onClick={() => downloadReport('pdf')}>
            Download PDF
          </Button>
          <Button type="primary" icon={<FileExcelOutlined />} disabled={!reportMonths.length} onClick={() => downloadReport('excel')} style={{ background: '#1d6f42', borderColor: '#1d6f42' }}>
            Download Excel
          </Button>
        </Space>
      </Card>

      {/* Settings: profile + password */}
      <Card title={<span><KeyOutlined /> Settings</span>} style={{ borderRadius: 14, marginTop: 16 }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Text strong><UserOutlined /> My Details</Text>
            <Divider style={{ margin: '12px 0' }} />
            <Form form={profileForm} layout="vertical" onFinish={saveProfile}>
              <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter your name' }]}>
                <Input prefix={<UserOutlined />} placeholder="Your name" />
              </Form.Item>
              <Form.Item name="phone" label="Phone">
                <Input prefix={<PhoneOutlined />} placeholder="Phone number" />
              </Form.Item>
              <Form.Item label="Login Email">
                <Input value={data.client.email} disabled prefix={<UserOutlined />} />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={savingProfile}>Save Details</Button>
              </Form.Item>
            </Form>
          </Col>
          <Col xs={24} lg={12}>
            <Text strong><LockOutlined /> Change Password</Text>
            <Divider style={{ margin: '12px 0' }} />
            <Form form={pwdForm} layout="vertical" onFinish={changePassword}>
              <Form.Item name="current_password" label="Current Password" rules={[{ required: true, message: 'Enter current password' }]}>
                <Input.Password prefix={<LockOutlined />} placeholder="Current password" />
              </Form.Item>
              <Form.Item name="new_password" label="New Password" rules={[{ required: true, message: 'Enter new password' }, { min: 6, message: 'At least 6 characters' }]}>
                <Input.Password prefix={<LockOutlined />} placeholder="New password" />
              </Form.Item>
              <Form.Item name="confirm_password" label="Confirm New Password" dependencies={['new_password']} rules={[{ required: true, message: 'Confirm your password' }]}>
                <Input.Password prefix={<LockOutlined />} placeholder="Confirm new password" />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit" icon={<KeyOutlined />} loading={savingPwd}>Change Password</Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Card>

      {/* Contact / Support */}
      <Card
        title={<span><MessageOutlined /> Contact / Support</span>}
        style={{ borderRadius: 14, marginTop: 16, marginBottom: 24 }}
        extra={msgs.filter((m) => m.sender === 'admin').length ? <Badge status="processing" text="Admin replies here" /> : null}
      >
        <Text type="secondary">Koi bhi request, dikkat ya sawaal ho to yahan admin ko bhej do.</Text>
        <div style={{ margin: '14px 0' }}>
          <Input.TextArea
            rows={3}
            value={msgBody}
            onChange={(e) => setMsgBody(e.target.value)}
            placeholder="Type your message to admin…"
            maxLength={1000}
          />
          <Button type="primary" icon={<SendOutlined />} loading={sendingMsg} onClick={sendMessage} style={{ marginTop: 10 }}>
            Send to Admin
          </Button>
        </div>
        <Divider style={{ margin: '12px 0' }}>Conversation</Divider>
        {msgs.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No messages yet" />
        ) : (
          <List
            dataSource={msgs}
            renderItem={(m) => {
              const mine = m.sender === 'client';
              return (
                <List.Item style={{ justifyContent: mine ? 'flex-end' : 'flex-start', border: 'none', padding: '6px 0' }}>
                  <div style={{
                    maxWidth: '78%',
                    background: mine ? 'linear-gradient(135deg,#667eea,#764ba2)' : '#f0f0f5',
                    color: mine ? '#fff' : '#222',
                    padding: '10px 14px', borderRadius: 12,
                  }}>
                    <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 2 }}>{mine ? 'You' : 'Admin'} · {m.created_at}</div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Card>
    </div>
  );
}
